import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../services/api";

const FieldTooltipContext = createContext(null);

const normalize = (value, fallback = "") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const composeKey = (section, fieldKey) =>
  `${normalize(section, "general")}::${normalize(fieldKey)}`;

const mapRowsToPageCache = (rows = []) => {
  const next = {};
  rows.forEach((row) => {
    const section = normalize(row.section || row.section_name, "general");
    const fieldKey = normalize(row.fieldKey || row.field_key);
    if (!fieldKey) return;
    next[composeKey(section, fieldKey)] = {
      id: row.id,
      page: normalize(row.page),
      section,
      fieldKey,
      tooltipText: String(row.tooltipText ?? row.tooltip_text ?? ""),
      isActive: row.isActive !== false && row.is_active !== false,
      updatedAt: row.updatedAt || row.updated_at || null,
    };
  });
  return next;
};

export const FieldTooltipProvider = ({ children }) => {
  const [pageCache, setPageCache] = useState({});
  const [loadingPages, setLoadingPages] = useState({});
  const inFlightByPage = useRef({});

  const ensurePageLoaded = useCallback(
    async (page, force = false) => {
      const normalizedPage = normalize(page);
      if (!normalizedPage) return;

      if (!force && pageCache[normalizedPage]) return;

      if (inFlightByPage.current[normalizedPage]) {
        await inFlightByPage.current[normalizedPage];
        return;
      }

      const request = (async () => {
        setLoadingPages((prev) => ({ ...prev, [normalizedPage]: true }));
        try {
          const response = await api.get("/field-tooltips", {
            params: { page: normalizedPage },
            skipGlobalLoading: true,
          });
          const rows = response.data?.data || [];
          const mapped = mapRowsToPageCache(rows);
          setPageCache((prev) => ({ ...prev, [normalizedPage]: mapped }));
        } catch (error) {
          console.error("Failed to load field tooltips:", error);
        } finally {
          setLoadingPages((prev) => ({ ...prev, [normalizedPage]: false }));
          delete inFlightByPage.current[normalizedPage];
        }
      })();

      inFlightByPage.current[normalizedPage] = request;
      await request;
    },
    [pageCache],
  );

  const getTooltipRecord = useCallback(
    (page, section, fieldKey) => {
      const normalizedPage = normalize(page);
      const normalizedFieldKey = normalize(fieldKey);
      if (!normalizedPage || !normalizedFieldKey) return null;

      const key = composeKey(section, normalizedFieldKey);
      return pageCache[normalizedPage]?.[key] || null;
    },
    [pageCache],
  );

  const getTooltipText = useCallback(
    (page, section, fieldKey) =>
      getTooltipRecord(page, section, fieldKey)?.tooltipText || "",
    [getTooltipRecord],
  );

  const saveTooltip = useCallback(async (payload) => {
    const page = normalize(payload?.page);
    const section = normalize(payload?.section, "general");
    const fieldKey = normalize(payload?.fieldKey || payload?.field_key);
    const tooltipText = String(
      payload?.tooltipText ?? payload?.tooltip_text ?? "",
    );

    if (!page || !fieldKey) {
      throw new Error("page and fieldKey are required");
    }

    const response = await api.put(
      "/field-tooltips",
      {
        page,
        section,
        field_key: fieldKey,
        tooltip_text: tooltipText,
      },
      { skipGlobalLoading: true },
    );

    const row = response.data?.data;
    if (row) {
      const key = composeKey(section, fieldKey);
      const mapped = mapRowsToPageCache([row])[key] || {
        id: row.id,
        page,
        section,
        fieldKey,
        tooltipText,
        isActive: true,
        updatedAt: row.updatedAt || row.updated_at || null,
      };

      setPageCache((prev) => ({
        ...prev,
        [page]: {
          ...(prev[page] || {}),
          [key]: mapped,
        },
      }));
    }

    return response.data?.data;
  }, []);

  const value = useMemo(
    () => ({
      ensurePageLoaded,
      getTooltipRecord,
      getTooltipText,
      saveTooltip,
      isPageLoading: (page) => Boolean(loadingPages[normalize(page)]),
    }),
    [
      ensurePageLoaded,
      getTooltipRecord,
      getTooltipText,
      loadingPages,
      saveTooltip,
    ],
  );

  return (
    <FieldTooltipContext.Provider value={value}>
      {children}
    </FieldTooltipContext.Provider>
  );
};

export const useFieldTooltips = () => {
  const context = useContext(FieldTooltipContext);
  if (!context) {
    throw new Error(
      "useFieldTooltips must be used within FieldTooltipProvider",
    );
  }
  return context;
};
